// src/components/page/home/HomePageFooter.jsx
import React, { useEffect, useState, useRef, Suspense } from 'react';
import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import StartPageSetter from '@/components/StartPageSetter';
import useInstallPrompt from '@/hooks/useInstallPrompt';

const UPDATE_INTERVAL = 60000; // 60 секунд (менше навантаження)
const VISIT_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 години

const LazyStatsDialog = React.lazy(() => import('@/components/page/home/StatsDialog'));

const HomePageFooter = () => {
  const [stats, setStats] = useState({ views: 0, visitors: 0 });
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const isFetchingRef = useRef(false);
  const intervalRef = useRef(null);

  const { canInstall, promptInstall } =
    typeof window !== 'undefined' ? useInstallPrompt() : { canInstall: false, promptInstall: () => {} };

  // helper: ensure we have persistent visitor id
  const getOrCreateVisitorId = () => {
    try {
      const key = 'visitor_id';
      let visitorId = localStorage.getItem(key);
      if (!visitorId) {
        visitorId = uuidv4();
        localStorage.setItem(key, visitorId);
      }
      return visitorId;
    } catch (e) {
      // якщо localStorage недоступний — fallback на рандомну строку (тимчасово)
      return `fallback-${Date.now()}`;
    }
  };

  // перевіряємо, чи потрібно надсилати visit (1x на 24 години)
  const shouldSendVisit = () => {
    try {
      const last = localStorage.getItem('visitor_last_sent'); // ISO timestamp
      if (!last) return true;
      const lastTs = Number(last);
      if (Number.isNaN(lastTs)) return true;
      return Date.now() - lastTs > VISIT_COOLDOWN_MS;
    } catch (e) {
      return true;
    }
  };

  const markVisitSent = () => {
    try {
      localStorage.setItem('visitor_last_sent', String(Date.now()));
    } catch (e) {
      // ignore
    }
  };

  const fetchStats = async () => {
    if (typeof window === 'undefined') return;
    if (isFetchingRef.current) return; // не запускаємо паралельні виклики
    isFetchingRef.current = true;

    try {
      const visitorId = getOrCreateVisitorId();

      // Надсилаємо POST у visits тільки якщо пройшов cooldown (не більше 1x/24h)
      if (shouldSendVisit()) {
        try {
          await supabase
            .from('visits')
            .insert([{ visitor_id: visitorId }], { returning: 'minimal' });
          markVisitSent();
        } catch (err) {
          // Якщо вставка не пройшла — просто логувати, не зупиняти UI
          console.error('Failed to insert visit:', err);
        }
      }

      // Завантажуємо статистику на сьогодні
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('visits_daily')
        .select('*')
        .eq('day', today)
        .single();

      if (!error && data) {
        setStats({
          views: Number(data.total_visits) || 0,
          visitors: Number(data.unique_visitors) || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      isFetchingRef.current = false;
    }
  };

  // контролюємо poll: старт/стоп в залежності від видимості вкладки
  useEffect(() => {
    // виклик один раз під час mount
    if (typeof window === 'undefined') return;

    const startPolling = () => {
      // одразу зробити виклик і поставить інтервал
      fetchStats();
      if (intervalRef.current) return;
      intervalRef.current = setInterval(fetchStats, UPDATE_INTERVAL);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    // стартуємо тільки якщо вкладка видима
    if (!document.hidden) startPolling();

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // пустий deps — монтується 1 раз

  return (
    <footer className="text-center mt-8 py-3 text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom duration-700 ease-out">
      <div className="mb-1">
        Розроблено з ❤️ Сергієм Поліщуком для Тальянківського коледжу
        {' | '}
        <a
          href="https://www.facebook.com/coach.Sergiy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-primary"
        >
          Мій Facebook
        </a>
      </div>

      <div className="mb-1">
        <a href="/about" className="underline hover:text-primary text-xs">
          Авторство та права на вебмодуль
        </a>
      </div>

      <div className="mt-2">
        <StartPageSetter label="Зробити стартовою цю сторінку" />

        {canInstall && (
          <button
            onClick={promptInstall}
            className="px-3 py-1 rounded-md border bg-primary text-white hover:opacity-90 transition"
          >
            📲 Встановити застосунок
          </button>
        )}
      </div>

      <div className="text-xs text-muted-foreground/70 mt-2">
        &copy; {new Date().getFullYear()} Розклад Вашого Коледжу. Всі права захищено.
      </div>

      <div className="text-xs text-muted-foreground/80 mt-1">
        👥 Унікальні сьогодні: {stats.visitors} | 👁️ Перегляди сьогодні: {stats.views}
        {' | '}
        <span
          onClick={() => setShowStatsDialog(true)}
          className="underline hover:text-primary ml-1 cursor-pointer inline-flex items-center gap-1"
        >
          📊 Статистика
        </span>
      </div>

      <Suspense fallback={<div className="text-xs mt-2">Завантаження...</div>}>
        <LazyStatsDialog
          open={showStatsDialog}
          onOpenChange={(open) => setShowStatsDialog(open)}
          showTitle={false}
        />
      </Suspense>
    </footer>
  );
};

export default HomePageFooter;
